/**
 * PassengerSilenceCounter — quantifies the information vacuum surrounding an
 * outbreak.
 *
 * Displays three factors:
 *   1. Days since detection.
 *   2. Passenger headcount (from outbreak.population_at_risk).
 *   3. Number of verified public social voices.
 *
 * The "silence score" (big number) is the product of all three. Each factor
 * is explained by a small-print line beneath the counter.
 *
 * Design rules (uncodixify-ui.md):
 *   - rounded-lg, shadow-sm — no lift, no shadow escalation.
 *   - No animate-pulse, no gradient text.
 *   - Logical CSS only (ms-*, me-*, text-start).
 *   - Touch targets ≥ 44 × 44 px via parent containers.
 *
 * RTL rules (rtl-support-arabic.md):
 *   - No physical left/right props.
 *   - No rtl:flex-row-reverse — the app sets dir="rtl" on <html>.
 *
 * Prompt #11 (passenger-silence-and-influencer-feed).
 */

import { usePassengerVoicesSummary } from "#/hooks/usePassengerVoices.js";
import type { TranslateFn } from "#/lib/i18n.js";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface PassengerSilenceCounterProps {
  slug: string;
  locale: string;
  t: TranslateFn;
}

// ---------------------------------------------------------------------------
// Small helper: format a number with locale-aware grouping (e.g. 1,432)
// ---------------------------------------------------------------------------

function fmt(n: number, locale: string): string {
  try {
    return new Intl.NumberFormat(locale === "ar" ? "ar-EG" : locale).format(n);
  } catch {
    return String(n);
  }
}

// ---------------------------------------------------------------------------
// Factor row — one line per dimension
// ---------------------------------------------------------------------------

function FactorRow({
  label,
  value,
  locale,
}: {
  label: string;
  value: number | null;
  locale: string;
}) {
  return (
    <div className="flex items-center justify-between gap-4 py-2 border-b border-border last:border-b-0">
      <span className="text-sm text-muted-foreground text-start">{label}</span>
      <span className="text-sm font-semibold text-foreground tabular-nums">
        {value !== null ? fmt(value, locale) : "—"}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function PassengerSilenceCounter({
  slug,
  locale,
  t,
}: PassengerSilenceCounterProps) {
  const { data, isLoading, isError } = usePassengerVoicesSummary({ slug });

  if (isLoading) {
    return (
      <section
        aria-label={t("passengerSilence:counter.ariaLabel")}
        className="rounded-lg border border-border bg-card shadow-sm p-6"
      >
        <p
          className="text-sm text-muted-foreground"
          aria-live="polite"
          aria-busy="true"
        >
          {t("passengerSilence:counter.loading")}
        </p>
      </section>
    );
  }

  if (isError || !data) {
    return null;
  }

  // Compute the silence score product. If any factor is null (unknown), we
  // still show the known factors but the product uses 0 as a sentinel.
  const days = data.daysSinceDetected;
  const headcount = data.passengerHeadcount;
  const voices = data.distinctVerifiedHandles;

  // Silence score: days × headcount × voices where voice count is inverted —
  // more voices = less silence. We display the raw count; the narrative copy
  // explains the meaning.
  const silenceScore =
    headcount !== null
      ? days * headcount * Math.max(1, voices)
      : null;

  return (
    <section
      id="passenger-silence"
      aria-labelledby="passenger-silence-heading"
      className="rounded-lg border border-border bg-card shadow-sm p-6 space-y-4"
    >
      <h2
        id="passenger-silence-heading"
        className="text-lg font-semibold text-foreground"
      >
        {t("passengerSilence:counter.heading")}
      </h2>

      {/* Big silence score */}
      {silenceScore !== null && (
        <p
          className="text-4xl font-bold tabular-nums text-foreground"
          aria-label={`${t("passengerSilence:counter.scoreAriaPrefix")} ${fmt(silenceScore, locale)}`}
        >
          {fmt(silenceScore, locale)}
        </p>
      )}

      {/* Factor breakdown */}
      <div className="space-y-0">
        <FactorRow
          label={t("passengerSilence:counter.factorDays")}
          value={days}
          locale={locale}
        />
        <FactorRow
          label={t("passengerSilence:counter.factorPassengers")}
          value={headcount}
          locale={locale}
        />
        <FactorRow
          label={t("passengerSilence:counter.factorVoices")}
          value={voices}
          locale={locale}
        />
      </div>

      {/* Explanatory note */}
      <p className="text-xs text-muted-foreground text-start">
        {t("passengerSilence:counter.note")}
      </p>

      {/* Last verified timestamp */}
      {data.lastVerifiedAt && (
        <p className="text-xs text-muted-foreground text-start">
          {t("passengerSilence:counter.lastVerified")}{" "}
          <time dateTime={data.lastVerifiedAt.toISOString()}>
            {data.lastVerifiedAt.toLocaleString(
              locale === "ar" ? "ar-EG" : locale,
              { dateStyle: "medium", timeStyle: "short" },
            )}
          </time>
        </p>
      )}
    </section>
  );
}
